'use client';

import { createContext, useCallback, useContext } from 'react';

import { useSession } from 'next-auth/react';

import { signOutAction } from '@/app/actions';

interface UserContextValue {
  user: { name: string | null; role: string | null };
  handleSignOut: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | null>(null);

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export function UserProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const { data: session } = useSession();

  const handleSignOut = useCallback(async () => {
    await signOutAction();
  }, []);

  const value: UserContextValue = {
    user: {
      name: session?.user?.name ?? null,
      role: (session?.role as string) ?? null,
    },
    handleSignOut,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
